import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { PostCard as PostCardType } from '@/types'

import { ArrowRight } from 'lucide-react'

import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { CLUSTER_FALLBACK, CLUSTER_GROUPS } from '@/constants/pillars'

interface ClusterListProps {
    posts: PostCardType[]
    className?: string
}

type Bucket = {
    name: string
    posts: PostCardType[]
}

// Groups a pillar's cluster articles by subtopic rather than listing them
// chronologically, so the hub reads as a structured guide and every cluster
// article gets a link from its parent.
const bucketize = (posts: PostCardType[]): Bucket[] => {
    const order = [...CLUSTER_GROUPS, CLUSTER_FALLBACK]

    const map = new Map<string, PostCardType[]>()
    for (const post of posts) {
        const key =
            post.clusterGroup && (CLUSTER_GROUPS as readonly string[]).includes(post.clusterGroup)
                ? post.clusterGroup
                : CLUSTER_FALLBACK
        const list = map.get(key)
        if (list) list.push(post)
        else map.set(key, [post])
    }

    return order
        .filter((name) => map.has(name))
        .map((name) => ({ name, posts: map.get(name) ?? [] }))
}

export const ClusterList: FC<ClusterListProps> = ({ posts, className }): ReactNode => {
    const buckets = bucketize(posts)

    if (buckets.length === 0) {
        return (
            <p className={cn('text-muted-foreground', className)}>
                The articles in this guide are being written. Check back soon.
            </p>
        )
    }

    return (
        <div className={cn('flex flex-col gap-12', className)}>
            {buckets.map((bucket) => (
                <section key={bucket.name}>
                    <h3 className='font-heading text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground'>
                        {bucket.name}
                    </h3>

                    <ul className='mt-5 flex flex-col divide-y divide-border border-t border-border'>
                        {bucket.posts.map((post) => (
                            <li key={post.id}>
                                <Link
                                    href={`/${post.pillarSlug}/${post.slug}`}
                                    className='group flex flex-col gap-1.5 py-5 transition-colors sm:flex-row sm:items-baseline sm:justify-between sm:gap-6'
                                >
                                    <span className='flex-1'>
                                        <span
                                            data-mdlfy-cms={`posts:${post.id}:title`}
                                            className='font-heading text-lg font-semibold leading-snug text-foreground transition-colors group-hover:text-primary'
                                        >
                                            {post.title}
                                        </span>
                                        <span
                                            data-mdlfy-cms={`posts:${post.id}:excerpt`}
                                            className='mt-1 block text-sm leading-relaxed text-muted-foreground'
                                        >
                                            {post.excerpt}
                                        </span>
                                    </span>

                                    <span className='flex shrink-0 items-center gap-2 text-xs text-muted-foreground'>
                                        <time dateTime={post.publishedAt}>
                                            {formatDate(post.publishedAt)}
                                        </time>
                                        <ArrowRight className='size-4 transition-transform group-hover:translate-x-1' />
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    )
}
